import { Chip, SxProps, Tooltip } from "@mui/material";
import { Public } from "@mui/icons-material";
import { hostingChipSx } from "./HostingChip";

/**
 * Marks a cluster as one half of a multi-region pair.
 *
 * Gated on `replicationRole && peerClusterName`, the same condition the Details tab uses for its
 * multiregion rows. Both halves matter: `replicationRole` alone is set transiently during peering
 * before a peer is recorded, so gating on it by itself would badge a cluster that is not yet
 * paired with anything.
 *
 * The role (PRIMARY/REPLICA) and the peering status live in the tooltip rather than the label.
 * They change under the reader -- a failover swaps them without any action on this page -- and a
 * badge that silently flips its text is worse than one that states the durable fact and offers the
 * volatile detail on hover.
 */
export const MultiRegionChip = ({
  cluster,
  sx = {},
}: {
  cluster: any;
  sx?: SxProps;
}) => {
  const replicationRole = cluster?.replicationRole;
  const peerClusterName = cluster?.peerClusterName;
  const peeringStatus = cluster?.peeringStatus;
  const multiRegionName = cluster?.multiRegionName;

  if (!replicationRole || !peerClusterName) {
    return null;
  }

  const tooltip = [
    `${replicationRole}${peeringStatus ? ` (${peeringStatus})` : ""}`,
    `peer: ${peerClusterName}`,
    multiRegionName ? `endpoint: ${multiRegionName}` : null,
  ]
    .filter(Boolean)
    .join(" — ");

  return (
    <Tooltip title={tooltip}>
      <Chip
        label="Multi-Region"
        icon={<Public />}
        color="info"
        variant="outlined"
        sx={{ ...hostingChipSx, ...sx }}
      />
    </Tooltip>
  );
};
